/**
 * Lead List Table Component
 * 
 * Material-UI DataGrid with server-side pagination, sorting, and inline actions
 * 
 * Requirements: 9.5, 11.4, 11.5, 15.1, 15.2
 * Task: 21.1
 */

import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams, GridSortModel } from '@mui/x-data-grid';
import { Lead, LeadStatus, LeadSource } from '../../types/lead';
import GSTVerificationBadge from './GSTVerificationBadge';

interface LeadListTableProps {
  leads: Lead[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onEdit: (leadId: string) => void;
  onDelete: (leadId: string) => void;
  onView: (leadId: string) => void;
}

/**
 * LeadListTable Component
 * 
 * Displays leads in a Material-UI DataGrid with:
 * - Server-side pagination
 * - Sortable columns (name, company, phone, status, source, created_at)
 * - Inline actions (view, edit, delete)
 * - GST verification status badge
 */
const LeadListTable: React.FC<LeadListTableProps> = ({
  leads,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onEdit,
  onDelete,
  onView,
}) => {
  // Status color mapping
  const getStatusColor = (status: LeadStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case LeadStatus.NEW:
        return 'primary';
      case LeadStatus.CONTACTED:
        return 'default';
      case LeadStatus.QUALIFIED:
        return 'warning';
      case LeadStatus.CONVERTED:
        return 'success';
      case LeadStatus.LOST:
        return 'error';
      default:
        return 'default';
    }
  };

  // Status label mapping
  const getStatusLabel = (status: LeadStatus): string => {
    switch (status) {
      case LeadStatus.NEW:
        return 'New';
      case LeadStatus.CONTACTED:
        return 'Contacted';
      case LeadStatus.QUALIFIED:
        return 'Qualified';
      case LeadStatus.CONVERTED:
        return 'Converted';
      case LeadStatus.LOST:
        return 'Lost';
      default:
        return status;
    }
  };

  // Source label mapping
  const getSourceLabel = (source: LeadSource): string => {
    switch (source) {
      case LeadSource.INDIAMART:
        return 'Indiamart';
      case LeadSource.INSTAGRAM:
        return 'Instagram';
      case LeadSource.FACEBOOK_ADS:
        return 'Facebook Ads';
      case LeadSource.MANUAL_ENTRY:
        return 'Manual Entry';
      default:
        return source;
    }
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Lead Information',
      flex: 1,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Typography 
              variant="body2" 
              fontWeight={600} 
              sx={{ 
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              {params.row.name}
            </Typography>
            <GSTVerificationBadge
              status={params.row.gst_verification_status}
              verifiedAt={params.row.gst_verified_at}
              iconOnly
            />
          </Box>
          {params.row.company && (
            <Typography 
              variant="body2"
              sx={{ 
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.8125rem',
                mb: 0.5,
                lineHeight: 1.5,
              }}
            >
              {params.row.company}
            </Typography>
          )}
          {params.row.email && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                color: 'text.disabled',
                fontSize: '0.75rem',
                lineHeight: 1.5,
              }}
            >
              {params.row.email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      sortable: true,
    },
    {
      field: 'interest_category',
      headerName: 'Interest/Query',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const { city, state } = params.row;
        const location = [city, state].filter(Boolean).join(', ');
        return (
          <Typography variant="body2" noWrap>
            {location || params.value || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 140,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {getSourceLabel(params.value)}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View">
              <ViewIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => onView(params.row.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => onEdit(params.row.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => onDelete(params.row.id)}
          showInMenu
        />,
      ],
    },
  ];

  // Handle sort model change
  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      if (sort) {
        onSortChange(field, sort as 'asc' | 'desc');
      }
    }
  };

  // Handle pagination model change
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    // DataGrid uses 0-indexed pages, but our API uses 1-indexed
    // So we need to add 1 when calling onPageChange
    if (model.page !== page) {
      onPageChange(model.page);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={leads}
        columns={columns}
        loading={loading}
        
        // Pagination configuration
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={totalCount}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 20, 50, 100]}
        
        // Sorting configuration
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        
        // Row configuration
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => 'auto'}
        
        // Hide footer if no data
        hideFooter={leads.length === 0 && !loading}
        
        sx={{
          minHeight: 400,
          '& .MuiDataGrid-cell': {
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row': {
            minHeight: '80px !important',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '2px solid',
            borderColor: 'divider',
            minHeight: '56px !important',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            backgroundColor: 'background.paper',
          },
          '& .MuiTablePagination-root': {
            overflow: 'visible',
            display: 'flex',
          },
          '& .MuiTablePagination-toolbar': {
            minHeight: '56px',
            paddingLeft: 2,
            paddingRight: 2,
          },
          '& .MuiTablePagination-actions': {
            marginLeft: 2,
            display: 'flex',
            gap: 0.5,
          },
          '& .MuiTablePagination-actions button': {
            padding: 1,
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
            fontSize: '0.875rem',
          },
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
            fontSize: '0.875rem',
          },
        }}
      />
    </Box>
  );
};

export default LeadListTable;
